import { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, Coins, Save, RotateCcw, TrendingUp, TrendingDown, Percent, Building2, Lock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import { useAuth } from "@/context/AuthContext";
import { api, apiErrorMessage, formatRupiah } from "@/lib/api";

const TIPE = ["Pendapatan", "Potongan", "Beban Perusahaan"];
const SIFAT = [
  { value: "Tetap", label: "Tetap (nominal)", hint: "Nominal Rupiah tetap setiap periode." },
  { value: "Persentase", label: "Persentase (dari Gaji Pokok)", hint: "Persen (0-100) dikalikan Gaji Pokok pegawai." },
  { value: "Berbasis Kehadiran", label: "Berbasis Kehadiran (Hari Hadir x Rate)", hint: "Tarif Rupiah dikalikan jumlah Hari Hadir pada periode." },
  { value: "Berbasis Lembur", label: "Berbasis Lembur (Jam Lembur x Rate)", hint: "Tarif Rupiah dikalikan total jam lembur aktual — menggantikan formula otomatis Kepmenaker." },
  { value: "Tidak Tetap", label: "Tidak Tetap (input bulanan)", hint: "Nilainya berubah tiap bulan — diisi lewat menu Komponen Tidak Tetap (manual/upload Excel)." },
  { value: "Tidak Tetap - Nominal", label: "Tidak Tetap - Nominal (fixed, on/off)", hint: "Nominal Rupiah tetap, TAPI bisa di-non-aktifkan per pegawai lewat toggle di Profil Payroll. Tidak menjadi pengali lembur/BPJS." },
  { value: "Tahunan - Upah Tetap", label: "Tahunan - Upah Tetap (auto: Gaji Pokok + Σ Tetap)", hint: "Muncul hanya pada payroll periode yang mencakup tanggal Anniversary (awal_bergabung) pegawai. Nilai auto = Gaji Pokok + Σ komponen sifat Tetap. Cocok untuk Tunjangan Cuti / THR." },
];

export function tipeBadgeClass(tipe) {
  if (tipe === "Potongan") return "bg-rose-50 text-rose-700 border-rose-100";
  if (tipe === "Beban Perusahaan") return "bg-indigo-50 text-indigo-700 border-indigo-100";
  return "bg-emerald-50 text-emerald-700 border-emerald-100";
}

function formatRibuan(v) {
  const digits = String(v ?? "").replace(/\D/g, "");
  if (!digits) return "";
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

function empty() {
  return {
    nama: "", kode: "", tipe: "Pendapatan", sifat: "Tetap",
    nilai_default: "", kena_pajak: false, urutan: "0", keterangan: "",
    dasar_potongan_absensi: false,
  };
}

export default function AdminKomponenGaji() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [saving, setSaving] = useState(false);

  const stats = useMemo(() => {
    const aktif = rows.filter((r) => r.is_active !== false);
    return {
      total: rows.length,
      pendapatan: aktif.filter((r) => r.tipe === "Pendapatan").length,
      potongan: aktif.filter((r) => r.tipe === "Potongan").length,
      beban: aktif.filter((r) => r.tipe === "Beban Perusahaan").length,
      pajak: aktif.filter((r) => r.kena_pajak).length,
    };
  }, [rows]);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/payroll/komponen");
      setRows(data);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  function openCreate() {
    setEditing(null);
    setForm(empty());
    setOpen(true);
  }
  function openEdit(r) {
    setEditing(r);
    setForm({
      nama: r.nama || "",
      kode: r.kode || "",
      tipe: r.tipe || "Pendapatan",
      sifat: r.sifat || "Tetap",
      nilai_default: r.nilai_default != null ? String(r.nilai_default) : "",
      kena_pajak: !!r.kena_pajak,
      dasar_potongan_absensi: !!r.dasar_potongan_absensi,
      urutan: String(r.urutan ?? 0),
      keterangan: r.keterangan || "",
      system_key: r.system_key || null,
    });
    setOpen(true);
  }

  async function save() {
    if (form.nama.trim().length < 2) {
      toast.error("Nama komponen minimal 2 karakter");
      return;
    }
    const nilai = parseFloat(form.nilai_default || "0");
    if (isNaN(nilai) || nilai < 0) {
      toast.error("Nilai default tidak valid");
      return;
    }
    if (form.sifat === "Persentase" && nilai > 100) {
      toast.error("Komponen Persentase maksimal 100% dari Gaji Pokok");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        nama: form.nama.trim(),
        kode: form.kode.trim() || null,
        tipe: form.tipe,
        sifat: form.sifat,
        nilai_default: nilai,
        kena_pajak: form.kena_pajak,
        dasar_potongan_absensi: form.dasar_potongan_absensi,
        urutan: parseInt(form.urutan || "0", 10) || 0,
        keterangan: form.keterangan.trim() || null,
      };
      if (editing) {
        await api.put(`/payroll/komponen/${editing.id}`, payload);
        toast.success("Komponen diperbarui");
      } else {
        await api.post("/payroll/komponen", payload);
        toast.success("Komponen ditambahkan");
      }
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  async function del(r) {
    if (!window.confirm(`Hapus komponen "${r.nama}"?`)) return;
    try {
      const { data } = await api.delete(`/payroll/komponen/${r.id}`);
      if (data.deactivated) toast.info(data.pesan);
      else toast.success("Komponen dihapus");
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  }

  async function aktifkan(r) {
    try {
      await api.post(`/payroll/komponen/${r.id}/aktifkan`);
      toast.success(`Komponen "${r.nama}" diaktifkan`);
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  }

  const isPersen = form.sifat === "Persentase";
  const isTidakTetap = form.sifat === "Tidak Tetap";
  const isTahunanUpah = form.sifat === "Tahunan - Upah Tetap";
  const sifatHint = SIFAT.find((s) => s.value === form.sifat)?.hint;

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto" data-testid="page-komponen-gaji">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-teal-700">Administrasi</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 mt-1 leading-tight">
            Master Komponen Gaji
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Definisi komponen payroll yang dinamis — Pendapatan/Potongan, sifat perhitungan, dan status pajak.
          </p>
        </div>
        <Button onClick={openCreate} className="gap-2 bg-teal-700 hover:bg-teal-800" data-testid="komponen-add-btn">
          <Plus size={16} /> Tambah Komponen
        </Button>
      </div>

      <div className="mt-6 grid grid-cols-2 lg:grid-cols-5 gap-3">
        <StatCard icon={Coins} label="Total Komponen" value={stats.total} tone="slate" testId="stat-komponen-total" />
        <StatCard icon={TrendingUp} label="Pendapatan Aktif" value={stats.pendapatan} tone="emerald" testId="stat-komponen-pendapatan" />
        <StatCard icon={TrendingDown} label="Potongan Aktif" value={stats.potongan} tone="rose" testId="stat-komponen-potongan" />
        <StatCard icon={Building2} label="Beban Perusahaan" value={stats.beban} tone="indigo" testId="stat-komponen-beban" />
        <StatCard icon={Percent} label="Kena Pajak" value={stats.pajak} tone="amber" testId="stat-komponen-pajak" />
      </div>

      <div className="mt-6 rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                {["Urut", "Komponen", "Tipe", "Sifat", "Nilai Default", "Pajak", "Status", ""].map((h, i) => (
                  <TableHead key={i} className={`uppercase text-[10px] tracking-widest font-bold text-slate-500 ${i === 7 ? "text-right" : ""}`}>
                    {h}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-500">Memuat…</TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-14">
                    <div className="mx-auto w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
                      <Coins size={22} />
                    </div>
                    <div className="mt-3 font-semibold text-slate-700">Belum ada komponen gaji</div>
                    <div className="text-xs text-slate-500 mt-1">
                      Mulai dengan menambahkan komponen seperti Tunjangan Jabatan, Uang Makan, atau Potongan BPJS.
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((r) => {
                  const isSystem = !!r.system_key;
                  return (
                  <TableRow key={r.id} data-testid={`komponen-row-${r.id}`} className={`border-b border-slate-100 hover:bg-slate-50/60 ${r.is_active === false ? "opacity-60" : ""}`}>
                    <TableCell className="font-mono-clock text-xs text-slate-500">{r.urutan ?? 0}</TableCell>
                    <TableCell>
                      <div className="font-semibold text-slate-900 flex items-center gap-1.5 flex-wrap">
                        {r.nama}
                        {isSystem && (
                          <span
                            className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-teal-700 text-white"
                            title="Komponen sistem — tidak dapat dihapus"
                            data-testid={`komponen-system-badge-${r.id}`}
                          >
                            <Lock size={9} /> Sistem
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-slate-500">
                        {r.kode ? <span className="font-mono-clock mr-2">{r.kode}</span> : null}
                        {r.keterangan}
                      </div>
                      {r.dasar_potongan_absensi ? (
                        <span className="mt-1 inline-block text-[10px] font-semibold text-teal-800 bg-teal-50 border border-teal-100 rounded px-1.5 py-0.5">
                          Dasar Potongan Absensi
                        </span>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${tipeBadgeClass(r.tipe)}`}>
                        {r.tipe}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-slate-700">
                      {r.sifat}
                      <div className="text-[10px] text-slate-400">{r.satuan}</div>
                    </TableCell>
                    <TableCell className="text-sm font-semibold text-slate-800 whitespace-nowrap">
                      {r.sifat === "Persentase" ? `${r.nilai_default ?? 0}%` : formatRupiah(r.nilai_default)}
                    </TableCell>
                    <TableCell>
                      {r.kena_pajak ? (
                        <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-100">Taxable</span>
                      ) : (
                        <span className="text-xs text-slate-400">Non-Tax</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {r.is_active === false ? (
                        <span className="text-[10px] font-semibold text-slate-500 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5">Nonaktif</span>
                      ) : (
                        <span className="text-[10px] font-semibold text-teal-800 bg-teal-50 border border-teal-100 rounded-full px-2 py-0.5">Aktif</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {r.is_active === false ? (
                        <button
                          onClick={() => aktifkan(r)}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-teal-800 hover:text-teal-900"
                          data-testid={`komponen-aktifkan-${r.id}`}
                        >
                          <RotateCcw size={13} /> Aktifkan
                        </button>
                      ) : (
                        <>
                          <button
                            onClick={() => openEdit(r)}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-teal-800 hover:text-teal-900 mr-3"
                            data-testid={`komponen-edit-${r.id}`}
                          >
                            <Pencil size={13} /> Edit
                          </button>
                          {/* Tombol Hapus dihilangkan (Keamanan Data — Feb 2026). */}
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="komponen-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Komponen Gaji" : "Tambah Komponen Gaji"}</DialogTitle>
            <DialogDescription>
              Komponen ini dapat diaktifkan per pegawai melalui tab Payroll di Master Pegawai.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {form.system_key && (
              <div className="sm:col-span-2 rounded-lg border border-teal-200 bg-teal-50/60 p-3 flex items-start gap-2" data-testid="komponen-system-notice">
                <Lock size={14} className="text-teal-700 mt-0.5" />
                <div className="text-[11px] text-teal-900 leading-relaxed">
                  <b>Komponen Sistem.</b> Tipe &amp; sifat dikunci karena menjadi dasar
                  kalkulasi payroll (Persentase, Lembur Kepmenaker, PPh 21, Tahunan).
                  Anda tetap dapat mengubah nama, kode, urutan, dan pengaturan pajak.
                </div>
              </div>
            )}
            <div className="sm:col-span-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Nama Komponen</Label>
              <Input
                className="mt-1.5"
                placeholder="Contoh: Tunjangan Jabatan"
                value={form.nama}
                onChange={(e) => setForm({ ...form, nama: e.target.value })}
                data-testid="input-komponen-nama"
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Kode (opsional)</Label>
              <Input
                className="mt-1.5"
                placeholder="TJ-JAB"
                value={form.kode}
                onChange={(e) => setForm({ ...form, kode: e.target.value })}
                data-testid="input-komponen-kode"
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Urutan Tampil</Label>
              <Input
                type="number"
                min="0"
                max="999"
                className="mt-1.5"
                value={form.urutan}
                onChange={(e) => setForm({ ...form, urutan: e.target.value })}
                data-testid="input-komponen-urutan"
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                Tipe {form.system_key && <span className="ml-1 text-teal-700">(terkunci)</span>}
              </Label>
              <Select value={form.tipe} onValueChange={(v) => setForm({ ...form, tipe: v })} disabled={!!form.system_key}>
                <SelectTrigger className="mt-1.5" data-testid="input-komponen-tipe"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIPE.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                Sifat {form.system_key && <span className="ml-1 text-teal-700">(terkunci)</span>}
              </Label>
              <Select value={form.sifat} onValueChange={(v) => setForm({ ...form, sifat: v, nilai_default: "" })} disabled={!!form.system_key}>
                <SelectTrigger className="mt-1.5" data-testid="input-komponen-sifat"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SIFAT.map((s) => (<SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                {isTidakTetap ? "Nilai Bulanan" : isPersen ? "Persentase Default" : "Nilai Default"}
              </Label>
              {isTidakTetap ? (
                <div className="mt-1.5 rounded-lg border border-sky-200 bg-sky-50 p-3 text-xs text-sky-900" data-testid="komponen-tidak-tetap-info">
                  Nilai komponen ini <b>diisi setiap bulan</b> melalui menu <b>Komponen Tidak Tetap</b>
                  (input manual atau unggah Excel), sehingga tidak memerlukan nilai default.
                </div>
              ) : isTahunanUpah ? (
                <div className="mt-1.5 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="komponen-tahunan-info">
                  Nilai komponen ini <b>dihitung otomatis</b> saat periode payroll mencakup
                  <b> tanggal anniversary </b> pegawai (field <code>awal_bergabung</code>).
                  Rumus: <b>Gaji Pokok + Σ komponen sifat "Tetap"</b> milik pegawai — nilai default tidak digunakan.
                </div>
              ) : (
                <div className="relative mt-1.5">
                {!isPersen ? (
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs font-semibold text-slate-400">Rp</span>
                ) : null}
                <Input
                  inputMode={isPersen ? "decimal" : "numeric"}
                  className={isPersen ? "" : "pl-8 font-mono-clock"}
                  placeholder={isPersen ? "0 - 100" : "0"}
                  value={isPersen ? form.nilai_default : formatRibuan(form.nilai_default)}
                  onChange={(e) => {
                    if (isPersen) {
                      const raw = e.target.value.replace(",", ".");

                      if (/^\d{0,3}(\.\d{0,2})?$/.test(raw)) {
                        setForm({ ...form, nilai_default: raw });
                      }
                    } else {
                      const raw = e.target.value.replace(/\D/g, "");
                      setForm({ ...form, nilai_default: raw.slice(0, 12) });
                    }
                  }}
                  data-testid="input-komponen-nilai"
                />
                {isPersen ? (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-slate-400">%</span>
                ) : null}
                </div>
              )}
              <p className="text-[11px] text-slate-500 mt-1.5">{sifatHint}</p>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Keterangan (opsional)</Label>
              <Input
                className="mt-1.5"
                value={form.keterangan}
                onChange={(e) => setForm({ ...form, keterangan: e.target.value })}
                data-testid="input-komponen-keterangan"
              />
            </div>
            <div className="sm:col-span-2 rounded-lg border border-slate-200 bg-slate-50/60 p-3 space-y-3">
              <label className="flex items-center gap-3 text-sm text-slate-700">
                <Switch
                  checked={form.kena_pajak}
                  onCheckedChange={(v) => setForm({ ...form, kena_pajak: v })}
                  data-testid="input-komponen-pajak"
                />
                <span>
                  <b>Kena Pajak (Taxable)</b>
                  <span className="block text-[11px] text-slate-500">
                    Komponen ini masuk dasar pengenaan pajak penghasilan.
                  </span>
                </span>
              </label>
              <label className="flex items-center gap-3 text-sm text-slate-700">
                <Switch
                  checked={form.dasar_potongan_absensi}
                  onCheckedChange={(v) => setForm({ ...form, dasar_potongan_absensi: v })}
                  data-testid="input-komponen-dasar-absensi"
                />
                <span>
                  <b>Dasar Potongan Absensi (Tunjangan Kehadiran)</b>
                  <span className="block text-[11px] text-slate-500">
                    Nilai komponen ini menjadi dasar potongan alpha &amp; keterlambatan (persentase diatur di Konfigurasi Kebijakan).
                  </span>
                </span>
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="komponen-cancel-btn">Batal</Button>
            <Button onClick={save} disabled={saving} className="bg-teal-700 hover:bg-teal-800 gap-1.5" data-testid="komponen-save-btn">
              <Save size={14} /> {saving ? "Menyimpan…" : "Simpan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, tone, testId }) {
  const tones = {
    slate: "bg-slate-50 text-slate-700 border-slate-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    rose: "bg-rose-50 text-rose-700 border-rose-200",
    indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testId}>
      <div className={`w-9 h-9 rounded-lg border flex items-center justify-center ${tones[tone]}`}>
        <Icon size={16} />
      </div>
      <div className="mt-3 text-2xl font-extrabold text-slate-900 leading-none">{value}</div>
      <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-bold mt-1">{label}</div>
    </div>
  );
}
